import React from "react";
import { Box } from "./Box";

export interface TableColumn<T = any> {
 key: string;
 header: string;
 render?: (item: T, index: number) => React.ReactNode;
 align?: "left" | "center" | "right";
}

export interface TableProps<T = any> {
 columns: TableColumn<T>[];
 data: T[];
 keyExtractor?: (item: T, index: number) => string | number;
 className?: string;
 striped?: boolean;
 hover?: boolean;
}

const alignClasses = {
 left: "text-left",
 center: "text-center",
 right: "text-right",
};

export const Table = <T,>({
 columns,
 data,
 keyExtractor,
 className = "",
 striped = false,
 hover = false,
}: TableProps<T>) => {
 const getKey = (item: T, index: number) => {
  if (keyExtractor) return keyExtractor(item, index);
  return (item as any)?.id || index;
 };

 return (
  <Box className={`overflow-x-auto ${className}`}>
   <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
     <tr>
      {columns.map((column) => (
       <th
        key={column.key}
        className={`
         px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider
         ${alignClasses[column.align || "left"]}
        `}
       >
        {column.header}
       </th>
      ))}
     </tr>
    </thead>
    <tbody className={`bg-white divide-y divide-gray-200 ${striped ? "[&>tr:nth-child(even)]:bg-gray-50" : ""}`}>
     {data.map((item, index) => (
      <tr
       key={getKey(item, index)}
       className={hover ? "hover:bg-gray-100 transition-colors" : ""}
      >
       {columns.map((column) => (
        <td
         key={column.key}
         className={`
          px-6 py-4 whitespace-nowrap text-sm text-gray-900
          ${alignClasses[column.align || "left"]}
         `}
        >
         {column.render
          ? column.render(item, index)
          : (item as any)[column.key]}
        </td>
       ))}
      </tr>
     ))}
    </tbody>
   </table>
  </Box>
 );
};

export default Table;

